import React, { useState, useCallback, useMemo } from "react";
import ReactDataGrid from 'react-data-grid';
import UserDetailModal from './UserDetailModal';

const DataGridUserTable = ({ 
  users, 
  loading, 
  pagination,
  onStatusChange, 
  onDelete,
  onPageChange,
  onSearchChange,
  onFilterChange,
  onSortChange,
  searchTerm = '',
  filters = {},
  sortConfig = { sortBy: '', sortOrder: 'asc' },
  // New props for selection
  selectedUsers = [],
  onSelectionChange,
  showSelection = false
}) => {
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(false);

  // Format relative time
  const formatRelativeTime = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const loginDate = new Date(date);
    const diffInHours = Math.floor((now - loginDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
  };

  // Get user initials
  const getUserInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // Handle view user details
  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowUserDetail(true);
  };

  // Handle column sort
  const handleSort = useCallback((columnKey) => {
    const sortableColumns = ['rowNumber', 'fullName', 'email', 'role', 'invitationType', 'isActive', 'lastLogin', 'createdAt'];
    if (!sortableColumns.includes(columnKey)) return;

    let newSortOrder = 'asc';
    if (sortConfig.sortBy === columnKey && sortConfig.sortOrder === 'asc') {
      newSortOrder = 'desc';
    }
    
    onSortChange && onSortChange(columnKey, newSortOrder);
  }, [sortConfig, onSortChange]);

  // Row Number Formatter
  const RowNumberFormatter = ({ row }) => {
    return (
      <div className="text-15 text-center fw-500">{row.rowNumber}</div>
    );
  };

  // Status Badge Formatter
  const StatusFormatter = ({ row }) => (
    <span className={`badge px-15 py-5 rounded-4 ${
      row.isActive ? 'bg-green-1 text-white' : 'bg-orange-1 text-white'
    }`}>
      {row.isActive ? 'Active' : 'Pending Activation'}
    </span>
  );

  // User Avatar and Name Formatter
  const UserFormatter = ({ row }) => (
    <div className="d-flex items-center">
      <div className="size-40 d-flex justify-center items-center bg-purple-3 rounded-full mr-15">
        <div className="text-12 fw-500 text-purple-1">
          {getUserInitials(row.firstName, row.lastName)}
        </div>
      </div>
      <div className="text-15 fw-500">{row.fullName}</div>
    </div>
  );

  // Actions Formatter
  const ActionsFormatter = ({ row }) => (
    <div className="d-flex items-center x-gap-5 actions-cell" onClick={(e) => e.stopPropagation()}>
      <button 
        onClick={() => onStatusChange(row._id, !row.isActive)}
        className={`button -sm fw-500 ${
          row.isActive 
            ? 'bg-orange-1 text-white' 
            : 'bg-green-1 text-white'
        }`}
        title={row.isActive ? 'Suspend User' : 'Activate User'}
      >
        {row.isActive ? 'Suspend' : 'Activate'}
      </button>
      <button 
        onClick={() => onDelete(row._id)}
        className="button -sm bg-red-1 text-white fw-500"
        title="Delete User"
      >
        Delete
      </button>
    </div>
  );

  // Last Login Formatter
  const LastLoginFormatter = ({ row }) => (
    <div className="text-15">
      {formatRelativeTime(row.lastLogin)}
    </div>
  );

  // Role Formatter
  const RoleFormatter = ({ row }) => (
    <div className="text-15 capitalize">{row.role}</div>
  );

  // Invitation Type Formatter
  const InvitationTypeFormatter = ({ row }) => {
    const type = row.invitationType || 'direct-signup';
    const label = type === 'email-invite' ? 'Email Invite' : 'Direct Signup';
    return (
      <div className="text-15">{label}</div>
    );
  };

  // Exams Formatter
  const ExamsFormatter = ({ row }) => {
    // Prefer server-provided examTitles if available; fallback to populated purchasedExams
    const labels = Array.isArray(row.examTitles) && row.examTitles.length > 0
      ? row.examTitles
      : (Array.isArray(row.purchasedExams) 
          ? row.purchasedExams.map((p) => p?.exam?.title).filter(Boolean)
          : []);
    if (!labels.length) {
      return <div className="text-14 text-light-1">None</div>;
    }
    return (
      <div className="d-flex items-center flex-wrap" style={{ gap: '6px', rowGap: '6px' }}>
        {labels.map((title, idx) => (
          <span
            key={idx}
            className="badge bg-purple-1 text-white"
            style={{
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '12px',
              lineHeight: '16px'
            }}
          >
            {title}
          </span>
        ))}
      </div>
    );
  };

  // Email Formatter
  const EmailFormatter = ({ row }) => (
    <div className="text-15">{row.email}</div>
  );

  // Selection Checkbox Formatter
  const SelectionFormatter = ({ row }) => (
    <div 
      className="d-flex justify-center items-center" 
      onClick={(e) => e.stopPropagation()}
      style={{ position: 'relative', zIndex: 9999, pointerEvents: 'auto' }}
    >
      <input
        type="checkbox"
        defaultChecked={selectedUsers.includes(row._id)}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => handleUserSelection(row._id, e.target.checked)}
        style={{ 
          width: 16, 
          height: 16,
          appearance: 'auto',
          WebkitAppearance: 'checkbox',
          MozAppearance: 'checkbox',
          accentColor: '#6f42c1',
          display: 'inline-block',
          visibility: 'visible',
          opacity: 1,
          position: 'relative',
          zIndex: 10000
        }}
      />
    </div>
  );

  // Header Selection Checkbox
  const HeaderSelectionFormatter = () => (
    <div className="d-flex justify-center items-center" style={{ position: 'relative', zIndex: 9999, pointerEvents: 'auto' }}>
      <input
        type="checkbox"
        checked={isAllCurrentPageSelected}
        ref={(input) => {
          if (input) input.indeterminate = isSomeCurrentPageSelected;
        }}
        onChange={(e) => handleSelectAllUsers(e.target.checked)}
        style={{ 
          width: 16, 
          height: 16,
          appearance: 'auto',
          WebkitAppearance: 'checkbox',
          MozAppearance: 'checkbox',
          accentColor: '#6f42c1',
          display: 'inline-block',
          visibility: 'visible',
          opacity: 1,
          position: 'relative',
          zIndex: 10000
        }}
      />
    </div>
  );

  // Helper function to get sort indicator
  const getSortIndicator = (columnKey) => {
    if (sortConfig.sortBy !== columnKey) return '';
    return sortConfig.sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  // Handle individual user selection
  const handleUserSelection = useCallback((userId, isSelected) => {
    if (!onSelectionChange) return;
    
    let newSelection;
    if (isSelected) {
      newSelection = [...selectedUsers, userId];
    } else {
      newSelection = selectedUsers.filter(id => id !== userId);
    }
    
    onSelectionChange(newSelection);
  }, [selectedUsers, onSelectionChange]);

  // Handle select all users on current page
  const handleSelectAllUsers = useCallback((isSelected) => {
    if (!onSelectionChange) return;
    
    const currentPageUserIds = users.map(user => user._id);
    let newSelection = [...selectedUsers];
    
    if (isSelected) {
      // Add all current page users to selection
      currentPageUserIds.forEach(userId => {
        if (!newSelection.includes(userId)) {
          newSelection.push(userId);
        }
      });
    } else {
      // Remove all current page users from selection
      newSelection = newSelection.filter(userId => !currentPageUserIds.includes(userId));
    }
    
    onSelectionChange(newSelection);
  }, [users, selectedUsers, onSelectionChange]);

  // Check if all current page users are selected
  const isAllCurrentPageSelected = useMemo(() => {
    if (!users.length) return false;
    return users.every(user => selectedUsers.includes(user._id));
  }, [users, selectedUsers]);

  // Check if some (but not all) current page users are selected
  const isSomeCurrentPageSelected = useMemo(() => {
    if (!users.length) return false;
    return users.some(user => selectedUsers.includes(user._id)) && !isAllCurrentPageSelected;
  }, [users, selectedUsers, isAllCurrentPageSelected]);

  // Column definitions
  const columns = useMemo(() => {
    const baseColumns = [];
    
    // Add selection column if showSelection is true
    if (showSelection) {
      baseColumns.push({
        key: 'selection',
        name: '',
        headerRenderer: HeaderSelectionFormatter,
        width: 50,
        formatter: SelectionFormatter,
        resizable: false,
        sortable: false
      });
    }
    
    // Add row number column
    baseColumns.push({
      key: 'rowNumber',
      name: '#',
      width: 60,
      formatter: RowNumberFormatter,
      resizable: false,
      sortable: false // Row numbers are visual only, not sortable
    });
    
    // Add remaining columns
    baseColumns.push(
      {
        key: 'fullName',
        name: `User${getSortIndicator('fullName')}`,
        width: 240,
        formatter: UserFormatter,
        resizable: true,
        sortable: true,
        headerCellClass: sortConfig.sortBy === 'fullName' ? 'sorted-column' : ''
      },
      {
        key: 'email',
        name: `Email${getSortIndicator('email')}`,
        width: 240,
        formatter: EmailFormatter,
        resizable: true,
        sortable: true,
        headerCellClass: sortConfig.sortBy === 'email' ? 'sorted-column' : ''
      },
      {
        key: 'role',
        name: 'Role',
        width: 100,
        formatter: RoleFormatter,
        resizable: true,
        sortable: true,
        headerCellClass: sortConfig.sortBy === 'role' ? 'sorted-column' : ''
      },
      {
        key: 'invitationType',
        name: `Invitation${getSortIndicator('invitationType')}`,
        width: 140,
        formatter: InvitationTypeFormatter,
        resizable: true,
        sortable: true,
        headerCellClass: sortConfig.sortBy === 'invitationType' ? 'sorted-column' : ''
      },
      {
        key: 'purchasedExams',
        name: 'Exams',
        width: 320,
        formatter: ExamsFormatter,
        resizable: true,
        sortable: false
      },
      {
        key: 'isActive',
        name: `Status${getSortIndicator('isActive')}`,
        width: 130,
        formatter: StatusFormatter,
        resizable: true,
        sortable: true,
        headerCellClass: sortConfig.sortBy === 'isActive' ? 'sorted-column' : ''
      },
      {
        key: 'lastLogin',
        name: `Last Login${getSortIndicator('lastLogin')}`,
        width: 130,
        formatter: LastLoginFormatter,
        resizable: true,
        sortable: true,
        headerCellClass: sortConfig.sortBy === 'lastLogin' ? 'sorted-column' : ''
      },
      {
        key: 'createdAt',
        name: 'Joined',
        width: 110,
        formatter: ({ row }) => (
          <div className="text-15">
            {new Date(row.createdAt).toLocaleDateString()}
          </div>
        ),
        resizable: true,
        sortable: false
      },
      {
        key: 'actions',
        name: 'Actions',
        width: 180,
        formatter: ActionsFormatter,
        sortable: false
      }
    );
    
    return baseColumns;
  }, [onStatusChange, onDelete, sortConfig, pagination, showSelection, selectedUsers, isAllCurrentPageSelected, isSomeCurrentPageSelected, handleUserSelection, handleSelectAllUsers]);

  // Handle cell click to show user details
  const handleCellClick = useCallback((args, event) => {
    // Don't show details if clicking on action buttons
    if (event.target.closest('.actions-cell')) {
      return;
    }
    // Don't show details if clicking on actions column
    if (args.column.key === 'actions') {
      return;
    }
    handleViewDetails(args.row);
  }, []);

  // Handle header click for sorting
  const handleHeaderClick = useCallback((event, column) => {
    if (column.sortable !== false) {
      handleSort(column.key);
    }
  }, [handleSort]);

  // Pagination component
  const PaginationControls = () => {
    if (!pagination) return null;

    const { currentPage, totalPages, totalUsers, hasNext, hasPrev } = pagination;
    const pageSize = 20; // Should match the pageSize in UserManagement
    
    return (
      <div className="d-flex justify-between items-center mt-20 px-20">
        <div className="text-15">
          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalUsers)} of {totalUsers} users
        </div>
        <div className="d-flex items-center x-gap-10">
          <button 
            className="button -sm bg-light-4 text-light-1 fw-500"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!hasPrev}
          >
            Previous
          </button>
          <span className="text-15 px-15">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            className="button -sm bg-light-4 text-light-1 fw-500"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!hasNext}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-white border-light rounded-16 px-30 py-30 shadow-4">
        {/* Header */}
        <div className="d-flex items-center justify-between mb-20">
          <div className="d-flex items-center">
            <div className="size-40 d-flex justify-center items-center bg-green-3 rounded-full mr-15">
              <i className="text-16 icon-activity text-green-1"></i>
            </div>
            <div>
              <h5 className="text-18 lh-1 fw-500">User Management</h5>
              {showSelection && selectedUsers.length > 0 && (
                <div className="text-14 text-purple-1 mt-5">
                  {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
                </div>
              )}
            </div>
          </div>
          <div className="d-flex items-center x-gap-15">
            {/* Search Input */}
            <div className="form-input -w-250">
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
            
            {/* Role Filter */}
            <select 
              className="form-input -w-120"
              value={filters.role || ''}
              onChange={(e) => onFilterChange({ ...filters, role: e.target.value })}
            >
              <option value="">All Roles</option>
              <option value="student">Student</option>
              <option value="instructor">Instructor</option>
              <option value="admin">Admin</option>
            </select>
            
            {/* Status Filter */}
            <select 
              className="form-input -w-120"
              value={filters.status || ''}
              onChange={(e) => onFilterChange({ ...filters, status: e.target.value })}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>

        {/* Data Grid */}
        <div className="data-grid-container" style={{ height: '800px' }}>
          {loading ? (
            <div className="d-flex justify-center items-center h-100">
              <div className="text-15">Loading users...</div>
            </div>
          ) : users.length === 0 ? (
            <div className="d-flex justify-center items-center h-100">
              <div className="text-15">No users found.</div>
            </div>
          ) : (
            <ReactDataGrid
              key={`grid-${showSelection}-${selectedUsers.join(',')}`}
              columns={columns}
              rowGetter={(i) => users[i]}
              rowsCount={users.length}
              onGridRowsUpdated={({fromRow, toRow, updated}) => {
                // Handle row updates if needed
                console.log('Row updated:', {fromRow, toRow, updated});
              }}
              enableCellSelect={true}
              enableRowSelect={true}
              rowSelection={{
                showCheckbox: false,
                enableShiftSelect: false,
                onRowsSelected: (rows) => {
                  setSelectedRows(new Set(rows.map(r => r.rowIdx)));
                },
                onRowsDeselected: (rows) => {
                  const newSelected = new Set(selectedRows);
                  rows.forEach(r => newSelected.delete(r.rowIdx));
                  setSelectedRows(newSelected);
                },
                selectBy: {
                  indexes: Array.from(selectedRows)
                }
              }}
              headerRowHeight={50}
              rowHeight={70}
              onGridSort={(sortColumn, sortDirection) => {
                // Normalize direction from grid and forward to server-driven sort
                const normalized = (sortDirection || '').toLowerCase() === 'desc' ? 'desc' : 'asc';
                onSortChange && onSortChange(sortColumn, normalized);
              }}
              minHeight={800}
            />
          )}
        </div>

        {/* Pagination */}
        <PaginationControls />
      </div>

      {/* User Detail Modal */}
      <UserDetailModal 
        user={selectedUser}
        isOpen={showUserDetail}
        onClose={() => {
          setShowUserDetail(false);
          setSelectedUser(null);
        }}
      />
    </>
  );
};

export default DataGridUserTable; 